/**
 * @file Non-invasive analytics observers for tracking user behavior and performance.
 */

import {
  observeFCPOncePerSession,
  observeVisibilityOncePerPageView,
  waitForElement
} from "./observation";
import { triggerEventOnce } from "./analytics";
import { checkIsThankyouPage } from "../client-state/orderUrlParams";

/**
 * Records a "checkout_form_view" event to the data layer when the last name field fully shows in the viewport.
 */
function observePaymentFormView(): void {
  waitForElement("#lastName", (element) => {
    observeVisibilityOncePerPageView(
      element,
      () => {
        if (!sessionStorage.getItem("checkoutFormViewInitialTimestamp")) {
          sessionStorage.setItem("checkoutFormViewInitialTimestamp", Date.now().toString());
        }
        const checkoutFormViewCount = (getCheckoutFormViewCount() + 1).toString();
        sessionStorage.setItem("checkoutFormViewCount", checkoutFormViewCount);

        triggerEventOnce("checkout_form_view", { checkout_form_view_count: checkoutFormViewCount });
      },
      { threshold: 1 }
    ); // 1: 100% of the element must be visible
  });
}

/**
 * Records a "checkout_form_contactable" event to the data layer the first time per browser session that either the
 * phone or email field is marked as valid.
 */
function observePaymentFormContactable(): void {
  // Only observe once per session.
  if (sessionStorage.getItem("checkoutFormContactableFlag") === "true") return;

  // Whichever element selector shows up first in the DOM will trigger the callback, which only fires once.
  let callbackFired = false;
  for (const sel of [
    'div[data-field-container="email"][data-field-is-valid="true"]',
    'div[data-field-container="phone"][data-field-is-valid="true"]'
  ]) {
    waitForElement(sel, () => {
      if (!callbackFired) {
        callbackFired = true;
        triggerEventOnce("checkout_form_contactable", {
          checkout_form_view_count: getCheckoutFormViewCount()
        });
        sessionStorage.setItem("checkoutFormContactableFlag", "true");
      }
    });
  }
}

/**
 * Tracks when the user experience begins, based on FCP (first-contentful-paint).
 * Does not fire on the thank you page.
 *
 * - The session initial timestamp is calculated only once per session.
 * - The observer is disconnected after FCP is detected.
 *
 */
function observeSessionInitialTimestamp(): void {
  // Don't fire on the thank you page.
  if (checkIsThankyouPage()) return;

  // The "observe once" logic already recoreds the FCP timestamp, so we don't need to do anything in the callback.
  observeFCPOncePerSession(() => {});
}

export function getCheckoutFormViewCount(removeFromSession = false): number {
  const count = sessionStorage.getItem("checkoutFormViewCount") || "0";

  removeFromSession && sessionStorage.removeItem("checkoutFormViewCount");

  return parseInt(count);
}

/**
 * Calculates the time to complete the checkout process from the First Contentful Paint (FCP) event in the session.
 *
 * The initial FCP timestamp is removed after calculation.
 *
 * @returns {number | null} Time from FCP to purchase in seconds, or null if the initial timestamp is not found.
 */
export function calculateCheckoutCompletionTime(): number | null {
  // This was recorded when the session started, even across page refreshes.
  const fcpInitialTimestamp = sessionStorage.getItem("fcpInitialTimestamp");

  // The initial FCP timestamp was not recorded; something went wrong.
  if (!fcpInitialTimestamp) return null;

  // Clear the value for this session.
  sessionStorage.removeItem("fcpInitialTimestamp");

  // Calculate the duration in seconds.
  return (Date.now() - parseInt(fcpInitialTimestamp)) / 1000;
}

/**
 * Calculates the time taken to complete the credit card payment ("checkout") form.
 *
 * This function retrieves the initial checkout form view timestamp from session storage, calculates the duration
 * from the current time, and then clears the initial timestamp from session storage.
 *
 * @returns {number} The time taken to complete the checkout form in seconds.
 *                   Returns -1 if the initial timestamp was not recorded.
 */
export function calculatePaymentFormCompletionTime(): number | null {
  // This was recorded when the last name field was first fully visible in the checkout form.
  const initialTimestamp = sessionStorage.getItem("checkoutFormViewInitialTimestamp");

  // The initial timestamp was not recorded; probably express checkout.
  if (!initialTimestamp) return null;

  // Clear the value for this session.
  sessionStorage.removeItem("checkoutFormViewInitialTimestamp");

  // Calculate the duration in seconds.
  return (Date.now() - parseInt(initialTimestamp)) / 1000;
}

/**
 * Clears the "checkoutFormContactableFlag" from the session storage.
 * This function removes the item with the key "checkoutFormContactableFlag"
 * from the session storage, effectively resetting the flag.
 */
export function clearCheckoutFormContactableFlag(): void {
  sessionStorage.removeItem("checkoutFormContactableFlag");
}

/**
 * Sets a flag in the session storage indicating that the address auto-complete feature has been used.
 */
export function setAddressAutoCompleteUsed(): void {
  sessionStorage.setItem("addressAutoCompleteUsed", "true");
}

/**
 * Checks if the address auto-complete feature has been used and removes the key from session storage.
 *
 * @returns {boolean} - `true` if the address auto-complete feature has been used, otherwise `false`.
 */
export function getAddressAutoCompleteUsed(): boolean {
  const wasUsed = sessionStorage.getItem("addressAutoCompleteUsed") === "true";
  sessionStorage.removeItem("addressAutoCompleteUsed");
  return wasUsed;
}

/**
 * Sets a flag in the session storage indicating that the browser auto-fill feature has been used.
 */
export function setBrowserAutoFillUsed(): void {
  sessionStorage.setItem("browserAutoFillUsed", "true");
}

/**
 * Checks if the browser auto-fill feature has been used and removes the key from session storage.
 *
 * @returns {boolean} - `true` if the browser auto-fill feature has been used, otherwise `false`.
 */
export function getBrowserAutoFillUsed(): boolean {
  const wasUsed = sessionStorage.getItem("browserAutoFillUsed") === "true";
  sessionStorage.removeItem("browserAutoFillUsed");
  return wasUsed;
}

/**
 * Sets the start timestamp for post-purchase tasks in session storage.
 * This should be called when post-purchase flows begin.
 */
export function setPostPurchaseStartTimestamp(): void {
  sessionStorage.setItem("postPurchaseStartTimestamp", Date.now().toString());
}

const pageLoadTimestamp = Date.now();

/**
 * Calculates the time taken to complete post-purchase tasks.
 *
 * This function retrieves the post-purchase start timestamp from session storage, calculates the duration
 * from the current time, and then clears the timestamp from session storage.
 *
 * @returns {number | null} The time taken to complete post-purchase tasks in seconds.
 *                         Returns null if the start timestamp was not recorded.
 */
export function calculatePostPurchaseTaskCompletionTime(): number | null {
  // This was recorded when the post-purchase tasks began
  const startTimestamp = sessionStorage.getItem("postPurchaseStartTimestamp");

  // The start timestamp was not recorded
  if (!startTimestamp) {
    // Fallback to time since page load
    return (Date.now() - pageLoadTimestamp) / 1000;
  }

  // Clear the value for this session
  sessionStorage.removeItem("postPurchaseStartTimestamp");

  // Calculate the duration in seconds
  return (Date.now() - parseInt(startTimestamp)) / 1000;
}

/**
 * Non-invasively observe events, conditions, timings, and behaviors for fine-grained analytics.
 */
export function attachAnalyticsObservers(): void {
  observePaymentFormView();
  observePaymentFormContactable();
  observeSessionInitialTimestamp();
}
